import { ReactElement, useState } from "react";
import { IHostSystemInformation, ISysContainer } from "./types";
import { Chip, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { formatBytes } from "./utils";
import StyledTableRow from "../../../components/styledTableRow";
import { IColumn, Order } from "../../../types";
import { formatDateTimeWithSecondsX, getComparator, stableSort } from "../../../system/utils";

interface IProps {
  status: IHostSystemInformation;
}

const columns: IColumn[] = [
  {
    id: "name",
    label: "Container",
    minWidth: 200
  },
  {
    id: "state",
    label: "State",
    minWidth: 100
  },
  {
    id: "started",
    label: "Started",
    minWidth: 200,
  },
  {
    id: "finished",
    label: "Finished",
    minWidth: 200,
  },
  {
    id: "restartCount",
    label: "Restart Count",
    minWidth: 80,
  },
  {
    id: "memUsage",
    label: "Mem. Usage",
    minWidth: 80,
  },
  {
    id: "memLimit",
    label: "Mem. Limit",
    minWidth: 80,
  },
  {
    id: "memPercent",
    label: "Mem. Percent",
    minWidth: 80,
  },
  {
    id: "cpuPercent",
    label: "CPU Percent",
    minWidth: 80,
  },
];

const ContainersComponent = ({ status }: IProps): ReactElement => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof ISysContainer>("memUsage");

  const createSortHandler = (property: keyof ISysContainer) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <div className="display-flex-column flex-grow-0 jus">
      <strong>Containers</strong>
      <Table stickyHeader={true}  size={"small"} className="justify-self-stretch">
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              return (
                <TableCell
                  key={`${column.id}`}
                  style={{ backgroundColor: "#b0bec5", minWidth: column.minWidth  }}>

                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={createSortHandler(column.id as keyof ISysContainer)}>
                    {column.label}
                    {orderBy === column.id
                      ? <span className="cell-visuallyHidden">
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </span>
                      : null}
                  </TableSortLabel>
                </TableCell>
              );
            })}
            <TableCell style={{ backgroundColor: "#b0bec5",  minWidth: 60 }}  />
          </TableRow>
        </TableHead>
        <TableBody>
          {stableSort<ISysContainer>(status.container, getComparator(order, orderBy))
            .map((container) => {

              return (
                <StyledTableRow hover={true} tabIndex={-1} key={`container-${container.name}`} >
                  <TableCell>
                    {container.name}
                  </TableCell>
                  <TableCell align="center">
                    <Chip color={container.state === "running" ? "success" : "error" } label={container.state} size="small" />
                  </TableCell>
                  <TableCell>
                    {formatDateTimeWithSecondsX(container.started)}
                  </TableCell>
                  <TableCell>
                    {container.started < container.finished && formatDateTimeWithSecondsX(container.finished) }
                  </TableCell>
                  <TableCell align="right">
                    {container.restartCount}
                  </TableCell>
                  <TableCell align="right">
                    {formatBytes(container.memUsage)}
                  </TableCell>
                  <TableCell align="right">
                    {formatBytes(container.memLimit)}
                  </TableCell>
                  <TableCell align="right">
                    {container.memPercent.toFixed(2)}%
                  </TableCell>
                  <TableCell align="right" style={{ background: container.cpuPercent >= 50 ? "red" : undefined }}>
                    {container.cpuPercent.toFixed(2)}%
                  </TableCell>
                </StyledTableRow>
              );
            })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ContainersComponent;